import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { audienceAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { ChartData, AIInsight, AIAnalysis } from './AnalyticsContext';

// Types
export interface AudienceState {
  demographics: {
    age: ChartData | null;
    gender: ChartData | null;
    location: ChartData | null;
  };
  growth: ChartData | null;
  engagement: ChartData | null;
  interests: ChartData | null;
  sentiment: ChartData | null;
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    demographics: {
      age: boolean;
      gender: boolean;
      location: boolean;
    };
    growth: boolean;
    engagement: boolean;
    interests: boolean;
    sentiment: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    demographics: {
      age: string | null;
      gender: string | null;
      location: string | null;
    };
    growth: string | null;
    engagement: string | null;
    interests: string | null;
    sentiment: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

// Action Types
export type AudienceAction =
  | { type: 'FETCH_AGE_DEMOGRAPHICS_START' }
  | { type: 'FETCH_AGE_DEMOGRAPHICS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_AGE_DEMOGRAPHICS_ERROR'; payload: string }
  | { type: 'FETCH_GENDER_DEMOGRAPHICS_START' }
  | { type: 'FETCH_GENDER_DEMOGRAPHICS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_GENDER_DEMOGRAPHICS_ERROR'; payload: string }
  | { type: 'FETCH_LOCATION_DEMOGRAPHICS_START' }
  | { type: 'FETCH_LOCATION_DEMOGRAPHICS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_LOCATION_DEMOGRAPHICS_ERROR'; payload: string }
  | { type: 'FETCH_GROWTH_START' }
  | { type: 'FETCH_GROWTH_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_GROWTH_ERROR'; payload: string }
  | { type: 'FETCH_ENGAGEMENT_START' }
  | { type: 'FETCH_ENGAGEMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_ENGAGEMENT_ERROR'; payload: string }
  | { type: 'FETCH_INTERESTS_START' }
  | { type: 'FETCH_INTERESTS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_INTERESTS_ERROR'; payload: string }
  | { type: 'FETCH_SENTIMENT_START' }
  | { type: 'FETCH_SENTIMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_SENTIMENT_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

// Initial State
const initialState: AudienceState = {
  demographics: {
    age: null,
    gender: null,
    location: null,
  },
  growth: null,
  engagement: null,
  interests: null,
  sentiment: null,
  aiAnalysis: {},
  loading: {
    demographics: {
      age: false,
      gender: false,
      location: false,
    },
    growth: false,
    engagement: false,
    interests: false,
    sentiment: false,
    aiAnalysis: {},
  },
  error: {
    demographics: {
      age: null,
      gender: null,
      location: null,
    },
    growth: null,
    engagement: null,
    interests: null,
    sentiment: null,
    aiAnalysis: {},
  },
};

const normalizeAIAnalysisData = (analysis: any): AIAnalysis => {
  if (!analysis) {
    return { insights: [] };
  }

  const rawInsights = analysis.insights;
  let insightsArray: any[] = [];

  if (Array.isArray(rawInsights)) {
    insightsArray = rawInsights;
  } else if (rawInsights && typeof rawInsights === 'object') {
    insightsArray = Object.values(rawInsights);
  }

  const normalizedInsights = insightsArray
    .filter(Boolean)
    .map((insight: any) => ({
      ...insight,
      icon: insight?.icon ?? '',
      color: insight?.color ?? '',
    }));

  return {
    ...analysis,
    insights: normalizedInsights,
  };
};

// Reducer
const audienceReducer = (state: AudienceState, action: AudienceAction): AudienceState => {
  switch (action.type) {
    case 'FETCH_AGE_DEMOGRAPHICS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, age: true }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, age: null }
        },
      };
    case 'FETCH_AGE_DEMOGRAPHICS_SUCCESS':
      return {
        ...state,
        demographics: { ...state.demographics, age: action.payload },
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, age: false }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, age: null }
        },
      };
    case 'FETCH_AGE_DEMOGRAPHICS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, age: false }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, age: action.payload }
        },
      };

    case 'FETCH_GENDER_DEMOGRAPHICS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, gender: true }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, gender: null }
        },
      };
    case 'FETCH_GENDER_DEMOGRAPHICS_SUCCESS':
      return {
        ...state,
        demographics: { ...state.demographics, gender: action.payload },
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, gender: false }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, gender: null }
        },
      };
    case 'FETCH_GENDER_DEMOGRAPHICS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, gender: false }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, gender: action.payload }
        },
      };

    case 'FETCH_LOCATION_DEMOGRAPHICS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, location: true }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, location: null }
        },
      };
    case 'FETCH_LOCATION_DEMOGRAPHICS_SUCCESS':
      return {
        ...state,
        demographics: { ...state.demographics, location: action.payload },
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, location: false }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, location: null }
        },
      };
    case 'FETCH_LOCATION_DEMOGRAPHICS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          demographics: { ...state.loading.demographics, location: false }
        },
        error: {
          ...state.error,
          demographics: { ...state.error.demographics, location: action.payload }
        },
      };

    case 'FETCH_GROWTH_START':
      return {
        ...state,
        loading: { ...state.loading, growth: true },
        error: { ...state.error, growth: null },
      };
    case 'FETCH_GROWTH_SUCCESS':
      return {
        ...state,
        growth: action.payload,
        loading: { ...state.loading, growth: false },
        error: { ...state.error, growth: null },
      };
    case 'FETCH_GROWTH_ERROR':
      return {
        ...state,
        loading: { ...state.loading, growth: false },
        error: { ...state.error, growth: action.payload },
      };

    case 'FETCH_ENGAGEMENT_START':
      return {
        ...state,
        loading: { ...state.loading, engagement: true },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_SUCCESS':
      return {
        ...state,
        engagement: action.payload,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: action.payload },
      };

    case 'FETCH_INTERESTS_START':
      return {
        ...state,
        loading: { ...state.loading, interests: true },
        error: { ...state.error, interests: null },
      };
    case 'FETCH_INTERESTS_SUCCESS':
      return {
        ...state,
        interests: action.payload,
        loading: { ...state.loading, interests: false },
        error: { ...state.error, interests: null },
      };
    case 'FETCH_INTERESTS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, interests: false },
        error: { ...state.error, interests: action.payload },
      };

    case 'FETCH_SENTIMENT_START':
      return {
        ...state,
        loading: { ...state.loading, sentiment: true },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_SUCCESS':
      return {
        ...state,
        sentiment: action.payload,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: action.payload },
      };

    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };

    default:
      return state;
  }
};

// Context
interface AudienceContextType {
  state: AudienceState;
  dispatch: React.Dispatch<AudienceAction>;
  fetchAgeDemographics: () => Promise<void>;
  fetchGenderDemographics: () => Promise<void>;
  fetchLocationDemographics: () => Promise<void>;
  fetchGrowthData: () => Promise<void>;
  fetchEngagementData: () => Promise<void>;
  fetchInterestsData: () => Promise<void>;
  fetchSentimentData: () => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
  fetchAllAudienceData: () => Promise<void>;
}

const AudienceContext = createContext<AudienceContextType | undefined>(undefined);

// Provider
interface AudienceProviderProps {
  children: ReactNode;
}

export const AudienceProvider: React.FC<AudienceProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(audienceReducer, initialState);
  const { business } = useAuth();

  const fetchAgeDemographics = useCallback(async () => {
    if (!business?._id) return;
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_START' });
      const response = await audienceAPI.getAgeDemographics(business._id);
      dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_AGE_DEMOGRAPHICS_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchGenderDemographics = useCallback(async () => {
    if (!business?._id) return;
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_START' });
      const response = await audienceAPI.getGenderDemographics(business._id);
      dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_GENDER_DEMOGRAPHICS_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchLocationDemographics = useCallback(async () => {
    if (!business?._id) return;
    try {
      dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_START' });
      const response = await audienceAPI.getLocationDemographics(business._id);
      dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_LOCATION_DEMOGRAPHICS_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchGrowthData = useCallback(async () => {
    if (!business?._id) return;
    try {
      dispatch({ type: 'FETCH_GROWTH_START' });
      const response = await audienceAPI.getGrowthData(business._id);
      dispatch({ type: 'FETCH_GROWTH_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_GROWTH_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchEngagementData = useCallback(async () => {
    if (!business?._id) return;
    try {
      dispatch({ type: 'FETCH_ENGAGEMENT_START' });
      const response = await audienceAPI.getEngagementData(business._id);
      dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_ENGAGEMENT_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchInterestsData = useCallback(async () => {
    if (!business?._id) return;
    try {
      dispatch({ type: 'FETCH_INTERESTS_START' });
      const response = await audienceAPI.getInterestsData(business._id);
      dispatch({ type: 'FETCH_INTERESTS_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_INTERESTS_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchSentimentData = useCallback(async () => {
    if (!business?._id) return;
    try {
      dispatch({ type: 'FETCH_SENTIMENT_START' });
      const response = await audienceAPI.getSentimentData(business._id);
      dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ 
        type: 'FETCH_SENTIMENT_ERROR', 
        payload: error instanceof Error ? error.message : 'Bilinmeyen hata' 
      });
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
    try {
      const response = await audienceAPI.getAIAnalysis(business._id, chartType);
      dispatch({ 
        type: 'FETCH_AI_ANALYSIS_SUCCESS', 
        payload: { chartType, data: normalizeAIAnalysisData(response.data.data) } 
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ 
          type: 'FETCH_AI_ANALYSIS_ERROR', 
          payload: { 
            chartType, 
            error: 'PREPARING' 
          } 
        });
      } else {
        dispatch({ 
          type: 'FETCH_AI_ANALYSIS_ERROR', 
          payload: { 
            chartType, 
            error: error instanceof Error ? error.message : 'Bilinmeyen hata' 
          } 
        });
      }
    }
  }, [business?._id]);

  // Tüm audience verisini tek seferde çekip state'e dağıtan fonksiyon
  const fetchAllAudienceData = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      // Tüm loading state'lerini başlat
      dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_START' });
      dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_START' });
      dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_START' });
      dispatch({ type: 'FETCH_GROWTH_START' });
      dispatch({ type: 'FETCH_ENGAGEMENT_START' });
      dispatch({ type: 'FETCH_INTERESTS_START' });
      dispatch({ type: 'FETCH_SENTIMENT_START' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'demographics' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'growth' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'engagement' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'interests' });
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: 'sentiment' });

      const response = await audienceAPI.getAllAudienceData(business._id, 'audience');
      // API'den gelen data bir array, ilk elemanı alıyoruz
      const dataArray = response.data.data;
      const audienceData = Array.isArray(dataArray) && dataArray.length > 0 ? dataArray[0] : dataArray;

      // Demographics data
      // API'den ageGroups olarak geliyor
      if (audienceData?.data?.demographics?.ageGroups) {
        dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_SUCCESS', payload: audienceData.data.demographics.ageGroups });
      } else {
        dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_ERROR', payload: 'PREPARING' });
      }

      if (audienceData?.data?.demographics?.gender) {
        dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_SUCCESS', payload: audienceData.data.demographics.gender });
      } else {
        dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_ERROR', payload: 'PREPARING' });
      }

      if (audienceData?.data?.demographics?.location) {
        dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_SUCCESS', payload: audienceData.data.demographics.location });
      } else {
        dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_ERROR', payload: 'PREPARING' });
      }

      // Growth data
      if (audienceData?.data?.growth) {
        dispatch({ type: 'FETCH_GROWTH_SUCCESS', payload: audienceData.data.growth });
      } else {
        dispatch({ type: 'FETCH_GROWTH_ERROR', payload: 'PREPARING' });
      }

      // Engagement data
      if (audienceData?.data?.engagement) {
        dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: audienceData.data.engagement });
      } else {
        dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'PREPARING' });
      }

      // Interests data
      if (audienceData?.data?.interests) {
        dispatch({ type: 'FETCH_INTERESTS_SUCCESS', payload: audienceData.data.interests });
      } else {
        dispatch({ type: 'FETCH_INTERESTS_ERROR', payload: 'PREPARING' });
      }

      // Sentiment data
      if (audienceData?.data?.sentiment) {
        dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: audienceData.data.sentiment });
      } else {
        dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'PREPARING' });
      }

      // AI Analysis
      // API'den demographic (singular) olarak geliyor, ancak state'te demographics (plural) kullanılıyor
      const analysisTypes = ['demographic', 'growth', 'engagement', 'interests', 'sentiment'];
      analysisTypes.forEach(chartType => {
        // API'den gelen chartType'ı state'teki chartType'a map et
        const stateChartType = chartType === 'demographic' ? 'demographics' : chartType;
        
        if (audienceData?.data?.aiAnalysis?.[chartType]) {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_SUCCESS', 
            payload: { chartType: stateChartType, data: normalizeAIAnalysisData(audienceData.data.aiAnalysis[chartType]) } 
          });
        } else {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType: stateChartType, error: 'PREPARING' } 
          });
        }
      });

    } catch (error: any) {
      // Hata durumunda tüm state'leri PREPARING olarak işaretle
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_GROWTH_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_INTERESTS_ERROR', payload: 'PREPARING' });
        dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'PREPARING' });
        ['demographics', 'growth', 'engagement', 'interests', 'sentiment'].forEach(chartType => {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: 'PREPARING' } 
          });
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen hata';
        dispatch({ type: 'FETCH_AGE_DEMOGRAPHICS_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_GENDER_DEMOGRAPHICS_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_LOCATION_DEMOGRAPHICS_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_GROWTH_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_INTERESTS_ERROR', payload: errorMessage });
        dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: errorMessage });
        ['demographics', 'growth', 'engagement', 'interests', 'sentiment'].forEach(chartType => {
          dispatch({ 
            type: 'FETCH_AI_ANALYSIS_ERROR', 
            payload: { chartType, error: errorMessage } 
          });
        });
      }
    }
  }, [business?._id]);

  const value: AudienceContextType = {
    state,
    dispatch,
    fetchAgeDemographics,
    fetchGenderDemographics,
    fetchLocationDemographics,
    fetchGrowthData,
    fetchEngagementData,
    fetchInterestsData,
    fetchSentimentData,
    fetchAIAnalysis,
    fetchAllAudienceData,
  };

  return (
    <AudienceContext.Provider value={value}>
      {children}
    </AudienceContext.Provider>
  );
};

// Hook
export const useAudience = (): AudienceContextType => {
  const context = useContext(AudienceContext);
  if (context === undefined) {
    throw new Error('useAudience must be used within an AudienceProvider');
  }
  return context;
};
